import { Select } from '@/components/ui';

/** What every People & HR area receives from the page. */
export interface HrAreaProps {
  /** `undefined` means every company the viewer may access. */
  companyIds?: string[];
  /** Hidden when only one company is in view, because every row would repeat it. */
  showCompany: boolean;
}

/** Heading, one sentence on what the area is for, and its controls. */
export function HrAreaHeader({
  title,
  description,
  controls,
}: {
  title: string;
  description: string;
  controls?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
      <div className="min-w-0 max-w-2xl">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">{description}</p>
      </div>
      {controls}
    </div>
  );
}

/**
 * Opens a Setup section: what the section is for and where it is used, with an
 * optional status badge and the section's main action.
 */
export function SetupSectionIntro({
  children,
  badge,
  action,
}: {
  children: React.ReactNode;
  badge?: React.ReactNode;
  action?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
      <p className="min-w-0 max-w-2xl text-sm leading-6 text-gray-600 dark:text-gray-300">
        {children}
      </p>
      {(badge || action) && (
        <div className="flex shrink-0 items-center gap-3">
          {badge}
          {action}
        </div>
      )}
    </div>
  );
}

/** A company picker for the screens that only make sense one company at a time. */
export function HrCompanySelect({
  id,
  label = 'Company',
  companies,
  value,
  onChange,
}: {
  id: string;
  label?: string;
  companies: Array<{ _id: string; name: string }>;
  value: string;
  onChange: (companyId: string) => void;
}): React.ReactElement | null {
  if (companies.length < 2) return null;
  return (
    <div className="w-full sm:w-72">
      <Select
        id={id}
        label={label}
        value={value}
        onChange={(next) => {
          if (next) onChange(next);
        }}
        options={companies.map((company) => ({ value: company._id, label: company.name }))}
        clearable={false}
        searchable={companies.length > 8}
        searchPlaceholder="Search companies…"
      />
    </div>
  );
}
